import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Property } from '@/types/property';
import {
  usePropertyRules,
  useCreatePropertyRule,
  useUpdatePropertyRule,
  useDeletePropertyRule,
  useReorderPropertyRules,
  PropertyRule,
} from '@/hooks/usePropertyRules';
import { useState } from 'react';
import PropertyRuleFormModal from './PropertyRuleFormModal';
import { GripVertical, Pencil, Trash2, Plus } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface PropertyRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  property: Property | null;
}

const PropertyRulesModal = ({ isOpen, onClose, property }: PropertyRulesModalProps) => {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<PropertyRule | null>(null);
  const [deleteRule, setDeleteRule] = useState<PropertyRule | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [rules, setRules] = useState<PropertyRule[]>([]);

  const { data: rulesData, isLoading } = usePropertyRules(property?.id || '');
  const createRule = useCreatePropertyRule();
  const updateRule = useUpdatePropertyRule();
  const deleteRuleMutation = useDeletePropertyRule();
  const reorderRules = useReorderPropertyRules();

  // Update local state when data changes
  useState(() => {
    if (rulesData) {
      setRules(rulesData);
    }
  });

  const handleCreateRule = (title: string) => {
    if (property) {
      createRule.mutate(
        { propertyId: property.id, title },
        {
          onSuccess: () => {
            setIsFormModalOpen(false);
          },
        }
      );
    }
  };

  const handleUpdateRule = (title: string) => {
    if (editingRule && property) {
      updateRule.mutate(
        { id: editingRule.id, title, propertyId: property.id },
        {
          onSuccess: () => {
            setIsFormModalOpen(false);
            setEditingRule(null);
          },
        }
      );
    }
  };

  const handleDeleteRule = () => {
    if (deleteRule && property) {
      deleteRuleMutation.mutate(
        { id: deleteRule.id, propertyId: property.id },
        {
          onSuccess: () => {
            setDeleteRule(null);
          },
        }
      );
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const updatedRules = [...(rulesData || [])];
    const [draggedRule] = updatedRules.splice(draggedIndex, 1);
    updatedRules.splice(dropIndex, 0, draggedRule);

    setRules(updatedRules);
    setDraggedIndex(null);

    if (property) {
      reorderRules.mutate({ rules: updatedRules, propertyId: property.id });
    }
  };

  const handleOpenEdit = (rule: PropertyRule) => {
    setEditingRule(rule);
    setIsFormModalOpen(true);
  };

  const handleOpenCreate = () => {
    setEditingRule(null);
    setIsFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setIsFormModalOpen(false);
    setEditingRule(null);
  };

  const displayRules = rules.length > 0 ? rules : rulesData || [];

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-2xl">Reglas de la casa</DialogTitle>
            <p className="text-sm text-muted-foreground">{property?.shortName || property?.title}</p>
          </DialogHeader>

          <div className="flex justify-end mb-4">
            <Button onClick={handleOpenCreate} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Agregar regla
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-12 bg-muted rounded animate-pulse" />
                ))}
              </div>
            ) : displayRules.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <p>No hay reglas configuradas</p>
                <p className="text-sm mt-2">Haz clic en "Agregar regla" para crear la primera</p>
              </div>
            ) : (
              <div className="space-y-2">
                {displayRules.map((rule, index) => (
                  <div
                    key={rule.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    className={`flex items-center gap-3 p-3 bg-muted/30 rounded-lg border transition-all ${
                      draggedIndex === index ? 'opacity-50' : 'hover:bg-muted/50'
                    }`}
                  >
                    <GripVertical className="h-5 w-5 text-muted-foreground cursor-grab active:cursor-grabbing" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{rule.title}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenEdit(rule)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteRule(rule)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <PropertyRuleFormModal
        isOpen={isFormModalOpen}
        onClose={handleCloseFormModal}
        onSave={editingRule ? handleUpdateRule : handleCreateRule}
        rule={editingRule}
      />

      <AlertDialog open={!!deleteRule} onOpenChange={() => setDeleteRule(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar regla?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. La regla "{deleteRule?.title}" será eliminada permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRule} className="bg-destructive hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PropertyRulesModal;
